/** ARĂTAREA: ce a atins asistentul, văzut înainte să fie gata.
 *
 *  DE CE EXISTĂ. Asistentul apasă aceleași pârghii ca mâna (v.
 *  `asistent/unelte.ts`), deci schimbarea lui apărea instantaneu — documentul
 *  vechi într-un cadru, cel nou în următorul. Pe o mutare de câțiva centimetri
 *  asta e chiar invizibilă: omul citește în chat că s-a mutat masa și nu vede
 *  pe planșă NIMIC schimbându-se, deci trebuie să caute singur ce s-a atins.
 *  Arătarea pune între cele două cadre ce ar fi făcut și o mână: întâi se vede
 *  CE e luat în primire, apoi se vede MUTAREA.
 *
 *  DOUĂ TIMPI, și numai atât:
 *    1. ALEGEREA — un halou albastru peste tot ce atinge schimbarea, cu
 *       desenul încă în starea de dinainte. Ce se ȘTERGE se vede aici pentru
 *       ultima oară, ca fantomă din planul vechi.
 *    2. MIȘCAREA — ce s-a mutat alunecă din poziția veche în cea nouă. Restul
 *       (ștergeri, adăugiri, schimbări de însușiri) nu alunecă nicăieri: o
 *       ștergere e o dispariție, iar o fereastră făcută mai lată n-are de unde
 *       să vină.
 *
 *  SE MĂSOARĂ PE PLANUL DERIVAT, nu pe document. Planul derivat e chiar ce se
 *  vede pe ecran — camerele ieșite din pereți, golurile cu tocul lor, piesele
 *  cu cadrul lor —, iar indicii lui sunt aceiași cu ai selecției (`Ales`).
 *  Măsurată pe document, o mutare de zid ar fi trebuit tradusă înapoi în ce se
 *  desenează, adică a doua oară aceeași derivare.
 *
 *  DESENUL SE MIȘCĂ DIN GEOMETRIE, nu dintr-un `transform` peste un element.
 *  Un zid dus cu transformul lui și-ar fi lăsat pe loc masca golurilor, hașura
 *  și cotele, fiindcă toate se socotesc din plan; aici se mută chiar planul dat
 *  desenului (`cuDeplasari`), deci se mută tot ce atârnă de el. Costul e un
 *  desen pe cadru, cât ține alunecarea — același lucru pe care îl face deja
 *  orice tragere de mouse.
 */
import { useEffect, useState } from "react";

import type { Ales } from "../Desen";
import type { PlanTronson, Pt } from "../doc";

/** Sub atât, două poziții sunt aceeași poziție: 5 mm. Rotunjirile derivării
 *  (arii, axe întinse) mișcă ultima zecimală, iar un halou pe zece pereți
 *  neatinși ar fi arătat exact pe dos decât trebuie. */
const EPS = 0.005;

/** CE I S-A ÎNTÂMPLAT UNUI ELEMENT.
 *
 *  `mutat` e singurul care alunecă; celelalte trei doar se luminează. */
export type FelSchimbare = "mutat" | "sters" | "adaugat" | "schimbat";

export type Tinta = {
  /** Elementul, în vocabularul selecției. INDICII SUNT AI PLANULUI NOU — afară
   *  de `sters`, unde nu mai există un plan nou în care să fie. */
  ales: Ales;
  fel: FelSchimbare;
  /** Unde era, și unde a ajuns. Numai pe `mutat`. */
  de?: Pt;
  la?: Pt;
};

export type Aratare = {
  /** Numărul arătării. O schimbare nouă o taie pe cea în lucru: asistentul
   *  poate cere două unelte în aceeași tură, iar a doua nu are de ce să aștepte
   *  sfârșitul primei. */
  nr: number;
  tinte: Tinta[];
  /** Planul de DINAINTE — din el se desenează fantoma celor șterse. */
  vechi: PlanTronson;
  /** Cine se mută, în ce ordine și în cât timp — v. `programeaza`. */
  program: Program;
};

// ---------------------------------------------------------------- geometrie -

const centrul = (inel: readonly Pt[]): Pt => {
  if (!inel.length) return [0, 0];
  let x = 0;
  let y = 0;
  for (const p of inel) { x += p[0]; y += p[1]; }
  return [x / inel.length, y / inel.length];
};

const departe = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Forma unui inel, fără poziția lui: vârfurile față de centru. Două ziduri cu
 *  aceeași formă la centre diferite au aceeași amprentă — adică s-a MUTAT. */
const forma = (inel: readonly Pt[]): string => {
  const c = centrul(inel);
  return inel.map((p) => `${r3(p[0] - c[0])},${r3(p[1] - c[1])}`).join(" ");
};

const mutat = (inel: readonly Pt[], d: Pt): Pt[] =>
  inel.map((p) => [p[0] + d[0], p[1] + d[1]] as Pt);

// ------------------------------------------------------------- potrivirea ---

/** O bucată de plan, pregătită de comparat. */
type Bucata = {
  ales: Ales;
  /** Identitatea scrisă, când există una (id-ul zidului). Gol = element
   *  poziţional, care se recunoaște doar după cum arată. */
  cheie: string;
  /** Cum arată, fără poziție. Egalitatea ei = «același lucru». */
  amprenta: string;
  poz: Pt;
  /** Poziția în lista lui, pentru potrivirea de pe urmă. */
  i: number;
};

/** VECHIUL ȘI NOUL, puse față în față.
 *
 *  Trei treceri, în ordinea încrederii:
 *    1. DUPĂ CHEIE, unde există una — un zid cu același id e același zid,
 *       oriunde ar fi ajuns în listă.
 *    2. DUPĂ AMPRENTĂ, perechea cea mai apropiată întâi. Așa, dintre trei
 *       ferestre la fel, cea ștearsă rămâne cea nepotrivită — perechile de
 *       distanță zero se iau primele, iar ce rămâne e chiar ce a dispărut.
 *    3. DUPĂ POZIȚIA ÎN LISTĂ, pentru ce n-a rămas potrivit: o piesă rotită
 *       sau o ușă întoarsă are altă amprentă, dar e tot a treia din listă.
 *       Ea nu alunecă nicăieri, doar se luminează.
 */
function potriveste(vechi: Bucata[], nou: Bucata[],
                    opt?: { faraMutare?: boolean }): Tinta[] {
  const tinte: Tinta[] = [];
  const libereV = new Set(vechi.keys());
  const libereN = new Set(nou.keys());

  const pereche = (iv: number, inou: number) => {
    libereV.delete(iv);
    libereN.delete(inou);
    const v = vechi[iv];
    const n = nou[inou];
    if (v.amprenta !== n.amprenta) {
      tinte.push({ ales: n.ales, fel: "schimbat" });
      return;
    }
    // FĂRĂ MUTARE: pentru camere, poziția e o consecință, nu o schimbare. Ele
    // se derivă din pereți, deci centrul unei camere se mută de fiecare dată
    // când se mută un zid al ei — iar ce s-a mutat acolo e zidul.
    if (!opt?.faraMutare && departe(v.poz, n.poz) > EPS) {
      tinte.push({ ales: n.ales, fel: "mutat", de: v.poz, la: n.poz });
    }
    // Aceeași amprentă la aceeași poziție: nimic de arătat.
  };

  // 1. cheile
  const dupaCheie = new Map<string, number>();
  for (const i of libereN) if (nou[i].cheie) dupaCheie.set(nou[i].cheie, i);
  for (const i of [...libereV]) {
    const c = vechi[i].cheie;
    if (!c) continue;
    const j = dupaCheie.get(c);
    if (j === undefined || !libereN.has(j)) continue;
    pereche(i, j);
  }

  // 2. amprentele, perechea cea mai apropiată întâi
  const candidati: { iv: number; inou: number; d: number }[] = [];
  for (const iv of libereV) {
    for (const inou of libereN) {
      if (vechi[iv].amprenta !== nou[inou].amprenta) continue;
      candidati.push({ iv, inou, d: departe(vechi[iv].poz, nou[inou].poz) });
    }
  }
  candidati.sort((a, b) => a.d - b.d);
  for (const c of candidati) {
    if (!libereV.has(c.iv) || !libereN.has(c.inou)) continue;
    pereche(c.iv, c.inou);
  }

  // 3. poziția în listă
  for (const iv of [...libereV]) {
    const inou = [...libereN].find((j) => nou[j].i === vechi[iv].i);
    if (inou === undefined) continue;
    pereche(iv, inou);
  }

  for (const iv of libereV) tinte.push({ ales: vechi[iv].ales, fel: "sters" });
  for (const inou of libereN) tinte.push({ ales: nou[inou].ales, fel: "adaugat" });
  return tinte;
}

// --------------------------------------------------------------- familiile --

const pereti = (plan: PlanTronson): Bucata[] => plan.pereti.map((w, i) => ({
  ales: { fel: "perete", i } as Ales,
  cheie: w.id ? `z:${w.id}` : "",
  amprenta: [w.kind, w.material, r3(w.grosime), r3(w.inaltime), r3(w.bulge),
             w.straturi.map((s) => `${s.kind}:${r3(s.grosime)}`).join("/"),
             forma(w.inel)].join("|"),
  poz: centrul(w.inel),
  i,
}));

const goluri = (plan: PlanTronson): Bucata[] => plan.goluri.map((g, i) => ({
  ales: { fel: "gol", fel2: g.fel === "usa" ? "doors" : "windows",
          i: g.idx } as Ales,
  cheie: "",
  // BALAMAUA ȘI SENSUL SUNT ÎN AMPRENTĂ, deși nu mișcă golul din loc: «întoarce
  // ușa» e o cerere obișnuită, iar fără ele schimbarea ieșea din socoteală ca
  // și cum nu s-ar fi întâmplat nimic.
  amprenta: [g.fel, g.model, r3(g.latime), r3(g.toc), g.balama,
             g.intrare ? "intrare" : "", g.spre ? forma([g.c, g.spre]) : "",
             g.parapet == null ? "" : r3(g.parapet)].join("|"),
  poz: g.c,
  i,
}));

const piese = (plan: PlanTronson): Bucata[] => plan.piese.map((f, i) => ({
  ales: { fel: "mobila", i } as Ales,
  cheie: "",
  amprenta: [f.kind, r3(f.w), r3(f.h), r3(f.deg), r3(f.z),
             f.oglX ? "X" : "", f.oglY ? "Y" : ""].join("|"),
  poz: f.c,
  i,
}));

const electrice = (plan: PlanTronson): Bucata[] => plan.electrice.map((e, i) => ({
  ales: { fel: "aparat", i: e.idx } as Ales,
  cheie: "",
  amprenta: [e.kind, r3(e.h), r3(e.deg), e.circuit,
             e.oglX ? "X" : "", e.oglY ? "Y" : ""].join("|"),
  poz: e.c,
  i,
}));

const partileNucleului = (plan: PlanTronson): Bucata[] =>
  (plan.nucleu?.parti ?? []).map((p, i) => ({
    ales: { fel: "nucleuParte", i } as Ales,
    cheie: "",
    amprenta: [p.kind, forma(p.inel)].join("|"),
    poz: centrul(p.inel),
    i,
  }));

/** CAMERELE SE COMPARĂ FĂRĂ GEOMETRIE, și asta e o alegere, nu o scăpare.
 *
 *  Camerele se derivă din pereți: orice zid mutat le schimbă inelul și aria,
 *  deci o comparație pe geometrie ar fi luminat jumătate de modul la fiecare
 *  mutare de despărțitură — adică ar fi arătat totul, adică nimic. Ce se
 *  luminează e camera căreia i s-a schimbat FELUL, pardoseala, tencuiala sau
 *  apartenența, plus cele apărute și dispărute. Mutarea zidului o arată
 *  zidul. */
const camere = (plan: PlanTronson): Bucata[] => plan.camere.map((c, i) => ({
  ales: { fel: "camera", i } as Ales,
  cheie: "",
  amprenta: [c.kind, c.side, c.flat ?? "", c.pardoseala, c.perete,
             c.afara ?? ""].join("|"),
  poz: c.centru,
  i,
}));

/** Câte lucruri se pot lumina deodată. Peste atâtea, haloul nu mai spune «uite
 *  ce am atins», ci acoperă planșa — iar o mobilare automată atinge tot
 *  modulul. Se taie lista, nu arătarea: mișcarea se vede oricum. */
const MAX_TINTE = 120;

/** Ce s-a schimbat între două planuri, în ordinea în care se vede pe ecran. */
export function diferenta(vechi: PlanTronson, nou: PlanTronson): Tinta[] {
  const tinte = [
    ...potriveste(pereti(vechi), pereti(nou)),
    ...potriveste(goluri(vechi), goluri(nou)),
    ...potriveste(piese(vechi), piese(nou)),
    ...potriveste(electrice(vechi), electrice(nou)),
    ...potriveste(partileNucleului(vechi), partileNucleului(nou)),
    ...potriveste(camere(vechi), camere(nou), { faraMutare: true }),
  ];
  // CONTURUL MODULULUI e un singur lucru, deci nu trece prin potrivire: ori e
  // altul, ori nu.
  if (forma(vechi.anvelopa) !== forma(nou.anvelopa)
      || departe(centrul(vechi.anvelopa), centrul(nou.anvelopa)) > EPS) {
    tinte.push({ ales: { fel: "anvelopa" }, fel: "schimbat" });
  }
  return tinte.slice(0, MAX_TINTE);
}


// ------------------------------------------------------------- alunecarea ---

/** Planul, cu ce se mută încă dus înapoi spre poziția veche.
 *
 *  `ramase[i]` e cât a mai rămas din drumul țintei `i`: 1 = obiectul e desenat
 *  unde era, 0 = unde a ajuns. Se atinge DOAR geometria celor pornite; restul
 *  planului e același obiect, ca desenul să nu recalculeze ce n-are de ce.
 *
 *  GOLURILE MERG CU ZIDUL LOR. Un zid mutat și o ușă rămasă pe loc ar fi tăiat
 *  masca golurilor în aer și ar fi lăsat zidul întreg — ușa se vede tăiată din
 *  zid tocmai fiindcă amândouă se socotesc din același plan. */
export function cuDeplasari(plan: PlanTronson, tinte: readonly Tinta[],
                            ramase: readonly number[]): PlanTronson {
  const dePerete = new Map<number, Pt>();
  const deGol = new Map<number, Pt>();
  const dePiesa = new Map<number, Pt>();
  const deAparat = new Map<number, Pt>();
  const deParte = new Map<number, Pt>();
  tinte.forEach((t, i) => {
    const r = ramase[i] ?? 0;
    if (t.fel !== "mutat" || !t.de || !t.la || r <= 0) return;
    const a = t.ales;
    if (!a) return;
    const d: Pt = [(t.de[0] - t.la[0]) * r, (t.de[1] - t.la[1]) * r];
    if (a.fel === "perete") dePerete.set(a.i, d);
    else if (a.fel === "mobila") dePiesa.set(a.i, d);
    else if (a.fel === "aparat") deAparat.set(a.i, d);
    else if (a.fel === "nucleuParte") deParte.set(a.i, d);
    else if (a.fel === "gol") {
      const j = plan.goluri.findIndex(
        (g) => g.idx === a.i
          && (g.fel === "usa" ? "doors" : "windows") === a.fel2);
      if (j >= 0) deGol.set(j, d);
    }
  });
  if (!dePerete.size && !deGol.size && !dePiesa.size && !deAparat.size
      && !deParte.size) return plan;

  // Golurile zidurilor mutate, dacă nu s-au mutat ele singure (o ușă dusă de pe
  // un zid pe altul are deplasarea ei, și ea bate).
  for (const [i, d] of dePerete) {
    const id = plan.pereti[i]?.id;
    if (!id) continue;
    plan.goluri.forEach((g, j) => {
      if (g.perete === id && !deGol.has(j)) deGol.set(j, d);
    });
  }

  return {
    ...plan,
    pereti: dePerete.size ? plan.pereti.map((w, i) => {
      const d = dePerete.get(i);
      if (!d) return w;
      const ax = w.ax ? (mutat(w.ax, d) as [Pt, Pt]) : w.ax;
      return { ...w, inel: mutat(w.inel, d), ax };
    }) : plan.pereti,
    goluri: deGol.size ? plan.goluri.map((g, j) => {
      const d = deGol.get(j);
      if (!d) return g;
      const p = (q: Pt): Pt => [q[0] + d[0], q[1] + d[1]];
      return { ...g, a: p(g.a), b: p(g.b), c: p(g.c),
               spre: g.spre ? p(g.spre) : g.spre };
    }) : plan.goluri,
    piese: dePiesa.size ? plan.piese.map((f, i) => {
      const d = dePiesa.get(i);
      return d ? { ...f, c: [f.c[0] + d[0], f.c[1] + d[1]] as Pt } : f;
    }) : plan.piese,
    electrice: deAparat.size ? plan.electrice.map((e) => {
      const d = deAparat.get(e.idx);
      return d ? { ...e, c: [e.c[0] + d[0], e.c[1] + d[1]] as Pt } : e;
    }) : plan.electrice,
    nucleu: deParte.size && plan.nucleu ? {
      ...plan.nucleu,
      parti: plan.nucleu.parti.map((p, i) => {
        const d = deParte.get(i);
        return d ? { ...p, inel: mutat(p.inel, d) } : p;
      }),
    } : plan.nucleu,
  };
}

// ----------------------------------------------------------------- haloul ---

/** Conturul pe care se pune haloul. `null` = ținta nu e în planul dat. */
export function conturulTintei(plan: PlanTronson, a: Ales): Pt[] | null {
  if (!a) return null;
  if (a.fel === "perete") return plan.pereti[a.i]?.inel ?? null;
  if (a.fel === "camera") return plan.camere[a.i]?.inel ?? null;
  if (a.fel === "anvelopa") return plan.anvelopa ?? null;
  if (a.fel === "nucleuParte") return plan.nucleu?.parti[a.i]?.inel ?? null;
  if (a.fel === "nucleu") {
    const r = plan.nucleu?.rect;
    return r ? [[r[0], r[1]], [r[2], r[1]], [r[2], r[3]], [r[0], r[3]]] : null;
  }
  if (a.fel === "mobila") {
    const f = plan.piese[a.i];
    return f ? dreptunghi(f.c, f.w, f.h, f.deg) : null;
  }
  if (a.fel === "aparat") {
    const e = plan.electrice.find((x) => x.idx === a.i);
    return e ? dreptunghi(e.c, 0.34, 0.34, 0) : null;
  }
  // O AXĂ n-are contur de arătat: ea E linia desenată, iar aprinderea ei se
  // face pe planșă (v. `Panza`).
  if (a.fel !== "gol") return null;
  // Golul: cât ține el din zid, pe toată grosimea gazdei.
  const lista = a.fel2;
  const g = plan.goluri.find(
    (x) => x.idx === a.i && (x.fel === "usa" ? "doors" : "windows") === lista);
  if (!g) return null;
  const gros = plan.pereti.find((w) => w.id === g.perete)?.grosime ?? 0.25;
  const dx = g.b[0] - g.a[0];
  const dy = g.b[1] - g.a[1];
  const l = Math.hypot(dx, dy) || 1;
  const nx = (-dy / l) * (gros / 2);
  const ny = (dx / l) * (gros / 2);
  return [[g.a[0] + nx, g.a[1] + ny], [g.b[0] + nx, g.b[1] + ny],
          [g.b[0] - nx, g.b[1] - ny], [g.a[0] - nx, g.a[1] - ny]];
}

function dreptunghi(c: Pt, w: number, h: number, deg: number): Pt[] {
  const r = (deg * Math.PI) / 180;
  const cs = Math.cos(r);
  const sn = Math.sin(r);
  return ([[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]] as Pt[])
    .map(([x, y]) => [c[0] + x * cs - y * sn, c[1] + x * sn + y * cs] as Pt);
}

// ------------------------------------------------------------------ timpul --
//
// CINCI SECUNDE, ORICÂT AR FI DE MUTAT. Arătarea e o explicație, nu un film:
// peste atât, omul care a cerut «mută toți pereții interiori» stă și se uită la
// un editor care nu-l ascultă. Deci bugetul e fix și se împarte:
//
//    o singură mutare  -> actul întreg, ~2 s cu tot cu alegere;
//    câteva            -> acte mai scurte, una după alta, fiecare cu mânuța ei;
//    zeci              -> derulare rapidă: mai multe într-un act, fără mânuță,
//                         fiindcă o mână care sare din zece în zece obiecte nu
//                         mai seamănă cu o mână.
//
// UNA CÂTE UNA, NU TOATE DEODATĂ, și asta e chiar cererea: mutările se văd cum
// le-ar face cineva cu mâna. Douăzeci de obiecte plecând în același timp e o
// animație de interfață; unul, apoi altul, e un gest.

/** Cât ține haloul singur, înainte de prima mutare. */
export const RASTIMP_ALEGERE = 700;
/** Cât se stinge haloul, la sfârșit. */
export const RASTIMP_STINGERE = 260;
/** Cât se aprinde haloul, la început. */
const RASTIMP_APRINDERE = 140;
/** Un act întreg: mânuța ajunge la obiect, apoi îl trage. */
const ACT_PLIN = 1050;
/** Sub atât un act nu mai e un gest, e o clipire — de aici încolo se pun mai
 *  multe obiecte în același act. */
const ACT_MIN = 130;
/** Tot ce se poate cheltui pe o arătare. */
export const RASTIMP_TOT = 5000;
/** Din act, cât ține apropierea mânuții; restul e tragerea. */
const APROPIEREA = 0.3;

/** Programul unei arătări: câte acte, cât ține fiecare, cine intră în care. */
export type Program = {
  /** Țintele mutate, grupate pe acte, în ordinea în care se joacă. */
  acte: number[][];
  /** Cât ține un act. */
  act: number;
  /** Cât ține tot, cu alegere și stingere. */
  total: number;
  /** Se desenează mânuța? Nu, când un act mută mai multe deodată. */
  cuMana: boolean;
};

/** MIȘCAREA E O ALEGERE A OMULUI, nu a noastră. Cine a cerut sistemului mai
 *  puțină mișcare primește arătarea fără alunecare și fără mânuță: haloul
 *  rămâne, fiindcă el spune CE s-a atins, iar asta nu e podoabă. */
function miscareaPermisa(): boolean {
  return !(typeof window !== "undefined" && window.matchMedia
           && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
}

/** Împarte mutările în acte, cu bugetul de cinci secunde în mână. */
export function programeaza(tinte: readonly Tinta[]): Program {
  const gol: Program = { acte: [], act: 0, cuMana: false,
                         total: RASTIMP_ALEGERE + RASTIMP_STINGERE };
  if (!miscareaPermisa()) return gol;
  const mutate = tinte.map((t, i) => ({ t, i }))
    .filter(({ t }) => t.fel === "mutat" && t.de && t.la)
    .map(({ i }) => i);
  if (!mutate.length) return gol;

  const buget = RASTIMP_TOT - RASTIMP_ALEGERE - RASTIMP_STINGERE;
  // Câte acte încap la durata minimă a unuia — de-aici iese câte obiecte
  // trebuie puse într-un act ca să se termine la timp.
  const maxActe = Math.max(1, Math.floor(buget / ACT_MIN));
  const peAct = Math.ceil(mutate.length / maxActe);
  const acte: number[][] = [];
  for (let i = 0; i < mutate.length; i += peAct) {
    acte.push(mutate.slice(i, i + peAct));
  }
  const act = Math.min(ACT_PLIN, buget / acte.length);
  return {
    acte,
    act,
    cuMana: peAct === 1,
    total: RASTIMP_ALEGERE + act * acte.length + RASTIMP_STINGERE,
  };
}

/** Un cadru al arătării, așa cum îl citește pânza. */
export type Cadru = {
  /** Cât a mai rămas din drum, pentru fiecare țintă: 1 = încă la locul vechi. */
  ramase: number[];
  /** Cât de tare se vede haloul. */
  opac: number;
  /** Se mai desenează fantomele celor șterse? Numai cât ține alegerea. */
  cuSterse: boolean;
  /** Unde e mânuța și dacă ține apăsat. `null` = nu se arată. */
  mana: { p: Pt; apasa: boolean } | null;
};

const STINS: Cadru = { ramase: [], opac: 0, cuSterse: false, mana: null };

/** Ceasul arătării, pe cadre.
 *
 *  STĂ ÎN PÂNZĂ, nu în editor, și tocmai de-aia e un hook: cât ține alunecarea
 *  se desenează la fiecare cadru, iar dacă starea ar fi fost sus, s-ar fi
 *  redesenat de șaizeci de ori pe secundă și panoul de proprietăți, și paleta,
 *  și bara — pentru o mișcare care se întâmplă într-un singur `<svg>`. */
export function useTranzitie(a: Aratare | null): Cadru {
  const [cadru, setCadru] = useState<Cadru>(STINS);
  useEffect(() => {
    if (!a) { setCadru(STINS); return; }
    const total = a.program.total;
    const t0 = performance.now();
    let ceas = 0;
    const pas = (acum: number) => {
      const dt = acum - t0;
      setCadru((v) => {
        const c = cadrul(a, dt);
        return aproape(v, c) ? v : c;
      });
      if (dt < total) ceas = requestAnimationFrame(pas);
      else setCadru(STINS);
    };
    // Cadrul dintâi se pune ACUM, nu la primul `requestAnimationFrame`: între
    // desenarea care aduce arătarea și cadrul următor trece un cadru întreg, iar
    // în el obiectul s-ar fi văzut o clipă deja mutat — adică exact săritura pe
    // care arătarea o înlocuiește.
    setCadru(cadrul(a, 0));
    ceas = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(ceas);
  }, [a]);
  return cadru;
}

/** Cadrul de la milisecunda `dt` — o funcție curată, deci și de probat. */
export function cadrul(a: Aratare, dt: number): Cadru {
  const { acte, act, total, cuMana } = a.program;
  const ramase: number[] = a.tinte.map((t) => (t.fel === "mutat" ? 1 : 0));
  const opac = dt < RASTIMP_APRINDERE ? Math.max(0, dt) / RASTIMP_APRINDERE
    : dt > total - RASTIMP_STINGERE
      ? Math.max(0, (total - dt) / RASTIMP_STINGERE) : 1;
  let mana: Cadru["mana"] = null;

  const deLaActe = dt - RASTIMP_ALEGERE;
  acte.forEach((grup, k) => {
    const inceput = k * act;
    const trecut = deLaActe - inceput;
    const t = act > 0 ? trecut / act : 1;
    for (const i of grup) {
      ramase[i] = t <= 0 ? 1 : t >= 1 ? 0 : 1 - tragere(t);
    }
    if (!cuMana || t < 0 || t > 1) return;
    // MÂNUȚA: întâi se apropie de obiect, apoi îl trage. Între acte rămâne pe
    // ultimul obiect lăsat, deci drumul ei se citește ca o mână care trece de la
    // unul la altul, nu ca o săritură.
    const tinta = a.tinte[grup[0]];
    if (!tinta?.de || !tinta.la) return;
    const anterior = k > 0 ? a.tinte[acte[k - 1][0]] : null;
    const dinainte = anterior?.la ?? tinta.de;
    if (t < APROPIEREA) {
      const u = lin(Math.max(0, t) / APROPIEREA);
      mana = { p: intre(dinainte, tinta.de, u), apasa: false };
    } else {
      const u = tragere(Math.min(1, t));
      mana = { p: intre(tinta.de, tinta.la, u), apasa: t <= 1 };
    }
  });
  return { ramase, opac, cuSterse: dt <= RASTIMP_ALEGERE, mana };
}

const intre = (a: Pt, b: Pt, t: number): Pt =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

const aproape = (a: Cadru, b: Cadru) =>
  a.cuSterse === b.cuSterse && Math.abs(a.opac - b.opac) < 0.02
  && !!a.mana === !!b.mana
  && (!a.mana || (a.mana.apasa === b.mana!.apasa
                  && departe(a.mana.p, b.mana!.p) < 0.002))
  && a.ramase.length === b.ramase.length
  && a.ramase.every((v, i) => Math.abs(v - b.ramase[i]) < 0.002);

/** TRAGEREA ÎNCEPE ȘI SE TERMINĂ LIN. O mână nu pornește în viteză și nu se
 *  oprește brusc; un obiect care face asta se citește ca o săritură, adică
 *  exact ce înlocuiește arătarea. */
const tragere = (t: number): number => {
  const x = Math.min(1, Math.max(0, (t - APROPIEREA) / (1 - APROPIEREA)));
  return x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2;
};

/** Pornire și oprire line, pe tot intervalul — pentru drumul mânuții. */
const lin = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 2 * x * x : 1 - ((-2 * x + 2) ** 2) / 2;
};
